import { Home, Newspaper, Menu, GitCompare } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";

interface BottomNavigationProps {
  onMenuClick?: () => void;
  menuOpen?: boolean;
  activeTab?: "home" | "news" | "profile" | "menu" | "prodotti" | "bacheca" | "confronta";
  variant?: "light" | "dark";
}

const liquidGlassCSS = `
.lg-nav-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 28px;
  background: rgba(200, 200, 200, 0.35);
  backdrop-filter: blur(40px) saturate(180%);
  -webkit-backdrop-filter: blur(40px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -1px 0 rgba(0, 0, 0, 0.04);
  position: fixed;
  bottom: calc(6px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  transition: opacity 0.3s ease;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  user-select: none;
  -webkit-user-select: none;
}
.lg-nav-bar.dark {
  background: rgba(40, 40, 50, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.28),
    inset 0 -1px 0 rgba(0, 0, 0, 0.08);
}
.lg-nav-item {
  position: relative;
  z-index: 1;
  width: 52px;
  height: 44px;
  border-radius: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.6);
  transition: color 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}
.lg-nav-bar.dark .lg-nav-item { color: rgba(255, 255, 255, 0.55); }
.lg-nav-item.pressed { transform: scale(0.92); transition: transform 0.1s ease; }
.lg-nav-item.active { color: rgba(0, 0, 0, 0.92); }
.lg-nav-bar.dark .lg-nav-item.active { color: rgba(255, 255, 255, 1); }
.lg-nav-item.menu-open { transform: rotate(90deg); }
.lg-nav-item.drag-hover { color: rgba(0, 0, 0, 0.95); }
.lg-nav-bar.dark .lg-nav-item.drag-hover { color: rgba(255, 255, 255, 1); }

/* ── INDICATOR base ───────────────────────────────────── */
.lg-nav-indicator {
  position: absolute;
  top: 6px;
  height: calc(100% - 12px);
  border-radius: 18px;
  pointer-events: auto;
  cursor: grab;
  z-index: 0;
  will-change: left, width, transform;
  transform: translateZ(0);
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(14px) saturate(150%);
  -webkit-backdrop-filter: blur(14px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow:
    inset 0 1.5px 0 rgba(255, 255, 255, 0.55),
    inset 0 -1px 0 rgba(0, 0, 0, 0.06),
    0 2px 10px rgba(0, 0, 0, 0.07);
  transition:
    left 0.22s cubic-bezier(0.25, 1, 0.5, 1),
    width 0.22s cubic-bezier(0.25, 1, 0.5, 1),
    transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
    background 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  user-select: none;
}
.lg-nav-bar.dark .lg-nav-indicator {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
}

/* ── HOLDING (long-press) ─────────────────────────────── */
.lg-nav-indicator.holding {
  transform: translateZ(0) scaleX(1.16) scaleY(1.4);
  background: rgba(255, 255, 255, 0.26);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow:
    inset 0 2px 0 rgba(255, 255, 255, 0.65),
    inset 0 -1.5px 0 rgba(0, 0, 0, 0.07),
    0 0 0 2px rgba(255, 255, 255, 0.18),
    0 6px 22px rgba(0, 0, 0, 0.14);
  transition:
    transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
    background 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;
}

@keyframes lgLiquidExpand {
  0%   { transform: translateZ(0) scaleX(1)    scaleY(1); }
  60%  { transform: translateZ(0) scaleX(1.20) scaleY(1.46); }
  100% { transform: translateZ(0) scaleX(1.16) scaleY(1.4); }
}
.lg-nav-indicator.expanding {
  animation: lgLiquidExpand 0.24s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  background: rgba(255, 255, 255, 0.26);
  border-color: rgba(255, 255, 255, 0.5);
}

@keyframes lgLiquidCollapse {
  0%   { transform: translateZ(0) scaleX(1.16) scaleY(1.4); }
  60%  { transform: translateZ(0) scaleX(0.99) scaleY(0.99); }
  100% { transform: translateZ(0) scaleX(1)    scaleY(1); }
}
.lg-nav-indicator.collapsing {
  animation: lgLiquidCollapse 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

/* ── DRAGGING — disable blur for buttery 60fps ─────────── */
.lg-nav-indicator.dragging {
  cursor: grabbing;
  transition: none !important;
  transform: translateZ(0) scaleX(1.1) scaleY(1.28);
  background: rgba(255, 255, 255, 0.32);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow:
    inset 0 2px 0 rgba(255, 255, 255, 0.65),
    inset 0 -1.5px 0 rgba(0, 0, 0, 0.07),
    0 0 0 2px rgba(255, 255, 255, 0.18),
    0 6px 20px rgba(0, 0, 0, 0.13);
}

/* ── SECTION LOADER ───────────────────────────────────── */
.lg-section-loader {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(22px) saturate(180%);
  -webkit-backdrop-filter: blur(22px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.32);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.lg-section-loader.visible {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}
.lg-section-loader::after {
  content: '';
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(120, 130, 150, 0.25);
  border-top-color: rgba(59, 130, 246, 0.95);
  border-right-color: rgba(59, 130, 246, 0.55);
  animation: lgLiquidSpin 0.75s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
@keyframes lgLiquidSpin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .lg-nav-indicator,
  .lg-nav-indicator.holding,
  .lg-nav-indicator.dragging,
  .lg-nav-indicator.expanding,
  .lg-nav-indicator.collapsing,
  .lg-nav-item,
  .lg-section-loader {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
}
`;

export const BottomNavigation = ({ onMenuClick, menuOpen = false, activeTab = "home", variant = "light" }: BottomNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const barRef = useRef<HTMLElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [draggedActive, setDraggedActive] = useState<string | null>(null);

  const dragState = useRef({
    holdTimer: null as ReturnType<typeof setTimeout> | null,
    isDragging: false,
    isHolding: false,
    pointerId: null as number | null,
    startX: 0,
    hasMoved: false,
  });

  const tabs = [
    { key: "news", icon: Newspaper, path: "/news" },
    { key: "home", icon: Home, path: "/" },
    { key: "confronta", icon: GitCompare, path: "/confronta-iphone" },
    { key: "menu", icon: Menu, path: null as string | null },
  ];

  const effectiveActive = draggedActive || activeTab;

  const moveIndicator = useCallback(() => {
    const bar = barRef.current;
    const el = itemsRef.current[effectiveActive];
    if (!bar || !el) return;
    const br = bar.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    setIndicator({ left: er.left - br.left, width: er.width });
  }, [effectiveActive]);

  useLayoutEffect(() => { moveIndicator(); }, [effectiveActive, moveIndicator]);
  useEffect(() => {
    const handler = () => moveIndicator();
    window.addEventListener("resize", handler);
    const t1 = setTimeout(handler, 50);
    const t2 = setTimeout(handler, 250);
    (document as any).fonts?.ready?.then(handler).catch(() => {});
    let ro: ResizeObserver | null = null;
    if (barRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(handler);
      ro.observe(barRef.current);
    }
    return () => {
      window.removeEventListener("resize", handler);
      clearTimeout(t1); clearTimeout(t2);
      ro?.disconnect();
    };
  }, [location.pathname, moveIndicator]);

  // Kill iOS 300ms tap delay globally (once)
  useEffect(() => {
    const noop = () => {};
    document.addEventListener("touchstart", noop, { passive: true });
    return () => document.removeEventListener("touchstart", noop);
  }, []);

  const findClosestTab = (clientX: number): string => {
    let closest = effectiveActive;
    let minDist = Infinity;
    for (const { key } of tabs) {
      const el = itemsRef.current[key];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const center = r.left + r.width / 2;
      const dist = Math.abs(clientX - center);
      if (dist < minDist) { minDist = dist; closest = key; }
    }
    return closest;
  };

  const highlightClosest = (clientX: number) => {
    const closest = findClosestTab(clientX);
    Object.entries(itemsRef.current).forEach(([k, el]) => {
      if (!el) return;
      if (k === closest) el.classList.add("drag-hover");
      else el.classList.remove("drag-hover");
    });
    if (closest !== draggedActive) setDraggedActive(closest);
  };

  const clearHighlights = () => {
    Object.values(itemsRef.current).forEach((el) => el?.classList.remove("drag-hover"));
  };

  const snapBackToActive = () => {
    const ind = indicatorRef.current;
    if (!ind) return;
    ind.classList.remove("holding", "dragging", "expanding");
    ind.classList.add("collapsing");
    ind.style.left = "";
    ind.style.width = "";
    setTimeout(() => ind.classList.remove("collapsing"), 360);
  };

  const activateTab = (key: string) => {
    const ind = indicatorRef.current;
    const target = tabs.find((t) => t.key === key);
    if (!target) return;

    if (ind) {
      ind.classList.remove("holding", "dragging", "expanding", "collapsing");
      ind.style.left = "";
      ind.style.width = "";
    }
    if (target.path) navigate(target.path);
  };

  // ── Indicator pointer handlers (long-press → drag) ──
  const onIndicatorPointerDown = (e: React.PointerEvent) => {
    const ind = indicatorRef.current;
    if (!ind) return;
    dragState.current.pointerId = e.pointerId;
    dragState.current.startX = e.clientX;
    dragState.current.hasMoved = false;
    dragState.current.isDragging = false;
    dragState.current.isHolding = false;

    // Instant micro-press feedback
    ind.style.transform = "scaleX(1.04) scaleY(1.1)";
    ind.style.transition = "transform 0.12s ease";

    dragState.current.holdTimer = setTimeout(() => {
      dragState.current.isHolding = true;
      dragState.current.isDragging = true;
      ind.style.transform = "";
      ind.style.transition = "";
      ind.classList.add("expanding");
      try { ind.setPointerCapture(e.pointerId); } catch {}
      if (navigator.vibrate) try { navigator.vibrate(8); } catch {}
      setTimeout(() => {
        ind.classList.remove("expanding");
        ind.classList.add("holding", "dragging");
      }, 320);
    }, 180);
  };

  const onIndicatorPointerMove = (e: React.PointerEvent) => {
    if (Math.abs(e.clientX - dragState.current.startX) > 4) {
      dragState.current.hasMoved = true;
    }
    if (dragState.current.hasMoved && !dragState.current.isHolding) {
      if (dragState.current.holdTimer) {
        clearTimeout(dragState.current.holdTimer);
        dragState.current.holdTimer = null;
      }
      const ind = indicatorRef.current;
      if (ind) { ind.style.transform = ""; ind.style.transition = ""; }
      return;
    }
    if (!dragState.current.isDragging) return;

    const bar = barRef.current;
    const ind = indicatorRef.current;
    if (!bar || !ind) return;
    e.preventDefault();

    const br = bar.getBoundingClientRect();
    const halfW = ind.offsetWidth / 2;
    let newLeft = e.clientX - br.left - halfW;
    const minLeft = 6;
    const maxLeft = br.width - ind.offsetWidth - 6;
    newLeft = Math.max(minLeft, Math.min(maxLeft, newLeft));
    ind.style.left = `${newLeft}px`;

    highlightClosest(e.clientX);
  };

  const onIndicatorPointerUp = (e: React.PointerEvent) => {
    const ind = indicatorRef.current;
    if (dragState.current.holdTimer) {
      clearTimeout(dragState.current.holdTimer);
      dragState.current.holdTimer = null;
    }
    const wasDragging = dragState.current.isDragging;
    dragState.current.isDragging = false;
    dragState.current.isHolding = false;
    dragState.current.pointerId = null;

    if (ind) {
      try { ind.releasePointerCapture(e.pointerId); } catch {}
      // clear inline micro-press
      ind.style.transform = "";
      ind.style.transition = "";
    }
    clearHighlights();

    if (!wasDragging) {
      if (ind) {
        ind.classList.remove("holding", "dragging", "expanding");
      }
      return;
    }

    const br = barRef.current?.getBoundingClientRect();
    if (!br || !ind) {
      setDraggedActive(null);
      snapBackToActive();
      return;
    }
    const pillLeft = parseFloat(ind.style.left) || 0;
    const pillCx = br.left + pillLeft + ind.offsetWidth / 2;
    const closest = findClosestTab(pillCx);

    setDraggedActive(null);

    if (closest === "menu") {
      onMenuClick?.();
      snapBackToActive();
    } else if (closest === activeTab) {
      snapBackToActive();
    } else {
      activateTab(closest);
    }
  };

  // ── Item touch handlers for instant feedback ──
  const onItemTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    target.classList.add("pressed");
  };
  const onItemTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    e.currentTarget.classList.remove("pressed");
  };

  return (
    <>
      <style>{liquidGlassCSS}</style>
      <nav ref={barRef} className={`lg-nav-bar ${variant}`}>
        <div
          ref={indicatorRef}
          className="lg-nav-indicator"
          style={{ left: indicator.left, width: indicator.width }}
          onPointerDown={onIndicatorPointerDown}
          onPointerMove={onIndicatorPointerMove}
          onPointerUp={onIndicatorPointerUp}
          onPointerCancel={onIndicatorPointerUp}
          aria-hidden
        />
        {tabs.map(({ key, icon: Icon, path }) => {
          const isMenu = key === "menu";
          return (
            <button
              key={key}
              ref={(el) => { itemsRef.current[key] = el; }}
              onTouchStart={onItemTouchStart}
              onTouchEnd={onItemTouchEnd}
              onTouchCancel={onItemTouchEnd}
              onClick={() => {
                if (dragState.current.isDragging || dragState.current.isHolding) return;
                if (isMenu) { onMenuClick?.(); return; }
                if (path && key !== activeTab) activateTab(key);
                else if (path) navigate(path);
              }}
              data-section={key}
              className={`lg-nav-item ${effectiveActive === key ? "active" : ""} ${isMenu && menuOpen ? "menu-open" : ""}`}
              aria-label={key}
            >
              <Icon className="w-5 h-5" />
            </button>
          );
        })}
      </nav>
      <div ref={loaderRef} className="lg-section-loader" aria-hidden />
    </>
  );
};
